import { useState, useEffect } from "react";
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import Tooltip from "./Tooltip";
import { db } from "../../firebase/config";
import { client } from "../../hooks/Client";
import notDeleted from "../../helpers/notDeleted";
import { isDeletedStatus } from "../synchronisations/syncDuplicateCheck";

const DragIconTableData = ({ effect = null, output = null, question = null, targetgroup = null, customEvent = null, renderInCell = true }) => {
  // pick whichever object is non-null
  const entity = effect ?? output ?? question ?? targetgroup ?? customEvent;
  const isEffect   = Boolean(effect);
  const isOutput   = Boolean(output);
  const isQuestion = Boolean(question);
  const isTargetgroup = Boolean(targetgroup);
  const isCustomEvent = Boolean(customEvent);

  // determine which Firestore field to query
  let fieldName;
  if (isEffect) fieldName = 'ProjectEffect';
  else if (isOutput) fieldName = 'ProjectOutput';
  else if (isQuestion) fieldName = 'ProjectQuestion';
  else if (isTargetgroup) fieldName = 'ProjectTargetgroup';
  else if (isCustomEvent) fieldName = 'CustomEvent';
  else fieldName = null;

  // state to track if paired
  const [paired, setPaired] = useState(false);

  useEffect(() => {
    // if there's no valid ID, reset and bail
    if (!entity?.ID) {
      setPaired(false);
      return;
    }

    const checkPaired = async () => {
      try {
        const snap = await db
          .collection('Synchronisations')
          .where(fieldName, '==', entity.ID)
          // Tenant-scope + secure against the Members-based Synchronisations rule.
          // AI-CONTEXT: See docs/pitfalls.md#security-synchronisations-members-tenant-gate
          .where('Members', 'array-contains', client)
          .get();
        // Only active pairings count — exclude soft-deleted syncs (ADR-008) and
        // rows whose Status marks them deleted.
        const activePairings = snap.docs.filter((d) => {
          const data = d.data();
          return notDeleted(data) && !isDeletedStatus(data.Status);
        });
        setPaired(activePairings.length > 0);
      } catch (err) {
        console.error("Error checking pairing:", err);
      }
    };

    checkPaired();
  }, [entity?.ID, fieldName]);

  // if neither effect, output nor question, render empty cell
  if (!entity) {
    return renderInCell ? <td /> : null;
  }

  // build style
  const style = {};
  if (entity.Standard === 'true') {
    style.borderLeft = '3px solid #fbb03b';
  }
  if (paired) {
    if (style.borderLeft) {
      style.boxShadow = 'inset 2px 0 0 #0368FE';
    } else {
      style.borderLeft = '3px solid #0368FE';
    }
  }

  // Dutch tooltip text variations
  // effect: het → Dit; output/question: de → Deze
  const noun     = isEffect ? 'effect' : isOutput ? 'output' : isQuestion ? 'vraag'  : isCustomEvent ? 'custom veld' : 'doelgroep';
  const det      = isEffect || isCustomEvent ? 'Dit' : 'Deze';
  const adjBase  = 'gestandaardiseerd';
  // for het‐nouns (effect) no -e, for de‐nouns (output & vraag) +e
  const adjIndef = isEffect ? adjBase : `${adjBase}e`;

  let tooltipText;
  if (entity.Standard === 'true' && paired) {
    // after demonstrative, adjective always gets -e
    tooltipText = `${det} gestandaardiseerde ${noun} is gekoppeld aan een portfolio account`;
  } else if (entity.Standard === 'true') {
    tooltipText = `Dit is een ${adjIndef} ${noun}`;
  } else if (paired) {
    tooltipText = `${det} ${noun} is gekoppeld aan een portfolio account`;
  } else {
    tooltipText = `Dit is een eigen ${noun}`;
  }

  const content = (
    <Tooltip content={tooltipText} top="-30px" left="25px">
      <DragIndicatorOutlinedIcon />
    </Tooltip>
  );

  if (!renderInCell) {
    return <span style={style}>{content}</span>;
  }

  return <td style={style}>{content}</td>;
};

export default DragIconTableData;


